/** A label that is actually tied to its input.
 *
 * There are only a handful of text inputs in the portal — a display name, a
 * token label, a track filter — and every one of them is wrapped here rather
 * than hand-rolled, so the `for`/`id` pair, the hint's `aria-describedby` and
 * the error's `aria-invalid` are written once and cannot be forgotten in the
 * seventh screen.
 */

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface FieldProps {
  label: ReactNode;
  /** Receives the id to put on the control. */
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
  /** The line under the control. */
  hint?: ReactNode;
  /** Replaces the hint and marks the control invalid. */
  error?: ReactNode;
  className?: string;
}

export function Field({ label, children, hint, error, className }: FieldProps) {
  const id = useId();
  const noteId = useId();
  const note = error ?? hint;

  return (
    <div className={['field', className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={id}>{label}</label>
      {children({ id, describedBy: note ? noteId : undefined })}
      {note ? (
        <div
          id={noteId}
          style={{
            marginTop: 4,
            fontSize: 11.5,
            color: error ? 'var(--color-error)' : 'var(--color-neutral-600)',
            textWrap: 'pretty',
          }}
        >
          {note}
        </div>
      ) : null}
    </div>
  );
}

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
}

/** The common case: one labelled `.input`. */
export function TextField({ label, hint, error, className, ...input }: TextFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {({ id, describedBy }) => (
        <input
          {...input}
          id={id}
          className="input"
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
        />
      )}
    </Field>
  );
}

export default Field;
